import React, { useState, useEffect, useContext } from 'react';
import { FaDatabase, FaTrash, FaCalendarAlt, FaArrowLeft, FaExclamationTriangle, FaDumpster } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { get_platforms_api, get_report_options_api, bulk_delete_data_api, count_data_to_delete_api, delete_artist_account_api } from '../controllers/dataController';
import { AlertContext } from '../context/AlertContext';
import SearchableSelect from '../components/SearchableSelect';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const ManageDataPage = () => {
    const { showAlert } = useContext(AlertContext);
    const [platforms, setPlatforms] = useState([]);
    const [labels, setLabels] = useState([]);
    const [artistNames, setArtistNames] = useState([]);
    const [loading, setLoading] = useState(false);

    // Confirmation Modal State
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [recordsToDelete, setRecordsToDelete] = useState(0);
    const [pendingDeleteData, setPendingDeleteData] = useState(null);

    const [artistIdToDelete, setArtistIdToDelete] = useState('');
    const [showDeleteArtistModal, setShowDeleteArtistModal] = useState(false);
    const [registeredArtists, setRegisteredArtists] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch all data concurrently
                const [
                    platformsData,
                    labelsData,
                    artistsData,
                    registeredArtistsData
                ] = await Promise.all([
                    get_platforms_api(),
                    get_report_options_api('label'),
                    get_report_options_api('artist_name'),
                    get_report_options_api('registered_account')
                ]);

                setPlatforms(platformsData);
                setLabels(labelsData);
                setArtistNames(artistsData);
                setRegisteredArtists(registeredArtistsData);
            } catch (error) {
                console.error("Error fetching options:", error);
                showAlert(error.message || "Failed to fetch options.", 'danger');
            }
        };
        fetchData();
    }, []);

    // Helper to format Date object to YYYY-MM
    const formatMonth = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    };

    // Individual states for each card
    const [monthData, setMonthData] = useState(null);
    
    const [platformId, setPlatformId] = useState('');
    const [platformMonth, setPlatformMonth] = useState(null);
    const [platformAllData, setPlatformAllData] = useState(false);
    
    const [labelName, setLabelName] = useState('');
    const [labelMonth, setLabelMonth] = useState(null);
    const [labelAllData, setLabelAllData] = useState(false);
    
    const [artistName, setArtistName] = useState('');
    const [artistMonth, setArtistMonth] = useState(null);
    const [artistAllData, setArtistAllData] = useState(false);

    const handleDeleteClick = async (deleteType) => {
        setLoading(true);
        const monthStr = deleteType === 'month' ? formatMonth(monthData) : (deleteType === 'platform' ? formatMonth(platformMonth) : (deleteType === 'label' ? formatMonth(labelMonth) : formatMonth(artistMonth)));
        
        const data = {
            delete_type: deleteType,
            month: monthStr,
            platform_id: platformId,
            label: labelName,
            artist_name: artistName,
            all_data: deleteType === 'platform' ? platformAllData : (deleteType === 'label' ? labelAllData : (deleteType === 'artist' ? artistAllData : false))
        };

        try {
            const response = await count_data_to_delete_api(data);
            setRecordsToDelete(response.count);
            setPendingDeleteData(data);
            setShowConfirmModal(true);
        } catch (error) {
            showAlert(error || "Failed to count data records", 'danger');
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!pendingDeleteData) return;
        
        setLoading(true);
        setShowConfirmModal(false);
        
        try {
            const response = await bulk_delete_data_api(pendingDeleteData);
            showAlert(response.message, 'success');
            
            // Reset fields based on delete type
            const { delete_type } = pendingDeleteData;
            if (delete_type === 'month') setMonthData(null);
            if (delete_type === 'platform') { setPlatformId(''); setPlatformMonth(null); setPlatformAllData(false); }
            if (delete_type === 'label') { setLabelName(''); setLabelMonth(null); setLabelAllData(false); }
            if (delete_type === 'artist') { setArtistName(''); setArtistMonth(null); setArtistAllData(false); }

        } catch (error) {
            showAlert(error || "Failed to delete data", 'danger');
        } finally {
            setLoading(false);
            setPendingDeleteData(null);
            setRecordsToDelete(0);
        }
    };

    const handleConfirmDeleteArtist = async () => {
        if (!artistIdToDelete) return;
        
        setLoading(true);
        setShowDeleteArtistModal(false);
        
        try {
            const response = await delete_artist_account_api({ artist_id: artistIdToDelete });
            showAlert(response.message, 'success');
            
            // Refresh artist list and reset selection
            const artistsData = await get_report_options_api('registered_account');
            setRegisteredArtists(artistsData);
            setArtistIdToDelete('');
    
        } catch (error) {
            showAlert(error.message || "Failed to delete artist account", 'danger');
        } finally {
            setLoading(false);
        }
    };

    const MonthPicker = ({ value, onChange, disabled }) => (
        <div className="relative w-full custom-month-picker">
            <DatePicker
                selected={value}
                onChange={onChange}
                dateFormat="MM/yyyy"
                showMonthYearPicker
                disabled={disabled}
                placeholderText="Select Month"
                className={`w-full pl-10 bg-background border border-border/50 rounded-md p-2 text-white focus:outline-none focus:border-primary ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                wrapperClassName="w-full"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaCalendarAlt className="text-muted-foreground" />
            </div>
        </div>
    );

    return (
        <div className="bg-background min-h-screen relative">
            <style>{`
                .react-datepicker-wrapper { width: 100%; }
                .react-datepicker {
                    background-color: #1f2937;
                    border: 1px solid #374151;
                    color: white;
                }
                .react-datepicker__header {
                    background-color: #111827;
                    border-bottom: 1px solid #374151;
                }
                .react-datepicker__header .react-datepicker__current-month,
                .react-datepicker-year-header {
                    color: white !important;
                }
                .react-datepicker__month-text {
                    color: #d1d5db;
                }
                .react-datepicker__month-text:hover {
                    background-color: #3b82f6;
                    color: #000000;
                }
                .react-datepicker__month-text--keyboard-selected{
                    background-color: #3b82f6 !important;
                    color: #000000 !important;
                }
                .react-datepicker__month--selected {
                    background-color: #3b82f6 !important;
                    color: #000000 !important;
                }
                .react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--upcoming), .react-datepicker__navigation--previous {
                    border-color: #9ca3af;
                }
            `}</style>
            
            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-4">
                    <div className="bg-card border border-border rounded-lg shadow-xl max-w-md w-full p-6 animate-scale-in">
                        <div className="flex items-center text-red-500 mb-4">
                            <FaExclamationTriangle size={24} className="mr-3" />
                            <h3 className="text-xl font-bold">Confirm Deletion</h3>
                        </div>
                        
                        <div className="bg-secondary/50 p-3 rounded-md mb-4 text-sm text-gray-300 space-y-1">
                            <p><span className="font-semibold text-gray-400">Action:</span> Delete by {pendingDeleteData?.delete_type}</p>
                            
                            {pendingDeleteData?.month && (
                                <p><span className="font-semibold text-gray-400">Month:</span> {pendingDeleteData.month}</p>
                            )}
                            
                            {pendingDeleteData?.platform_id && (
                                <p><span className="font-semibold text-gray-400">Platform ID:</span> {pendingDeleteData.platform_id}</p>
                            )}
                            
                            {pendingDeleteData?.label && (
                                <p><span className="font-semibold text-gray-400">Label:</span> {pendingDeleteData.label}</p>
                            )}
                            
                            {pendingDeleteData?.artist_name && (
                                <p><span className="font-semibold text-gray-400">Artist:</span> {pendingDeleteData.artist_name}</p>
                            )}

                            {pendingDeleteData?.all_data && (
                                <p className="text-yellow-400 font-bold mt-2">⚠ Deleting ALL time data for this selection!</p>
                            )}
                        </div>

                        <p className="text-foreground mb-2 text-center text-lg">
                            Records found: <span className="font-bold text-red-400">{recordsToDelete}</span>
                        </p>
                        
                        <p className="text-muted-foreground text-sm mb-6 text-center">
                            This action cannot be undone. Are you absolutely sure?
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button 
                                onClick={() => setShowConfirmModal(false)}
                                className="px-4 py-2 rounded-md bg-secondary text-foreground hover:bg-secondary/80 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={confirmDelete}
                                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors font-bold"
                            >
                                Confirm Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Header />
            <div className="pt-8 py-6 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
                <div className="flex items-center mb-8">
                    <Link to="/admin_dashboard" className="text-muted-foreground hover:text-primary mr-4 transition-colors">
                        <FaArrowLeft size={20} />
                    </Link>
                    <h1 className="text-2xl font-bold flex items-center text-foreground">
                        <FaTrash className="mr-3 text-primary" /> Delete Data
                    </h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Delete by Month */}
                    <div className="bg-gradient-card border border-border/50 p-6 rounded-lg shadow-lg flex flex-col h-full">
                        <div className="mb-4">
                            <h3 className="text-lg font-semibold text-foreground mb-1">1. Delete by Month</h3>
                            <p className="text-xs text-muted-foreground">Removes all records for the selected month across all platforms.</p>
                        </div>
                        <div className="flex-grow space-y-4">
                            <label className="block text-sm font-medium text-muted-foreground">Select Month</label>
                            <MonthPicker value={monthData} onChange={date => setMonthData(date)} />
                        </div>
                        <button
                            onClick={() => handleDeleteClick('month')}
                            disabled={loading || !monthData}
                            className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FaTrash className="mr-2" /> Delete Month Data
                        </button>
                    </div>

                    {/* Delete by Platform */}
                    <div className="bg-gradient-card border border-border/50 p-6 rounded-lg shadow-lg flex flex-col h-full">
                        <div className="mb-4">
                            <h3 className="text-lg font-semibold text-foreground mb-1">2. Delete by Platform</h3>
                            <p className="text-xs text-muted-foreground">Removes data association with a specific platform, for a month or all time.</p>
                        </div>
                        <div className="flex-grow space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Select Platform</label>
                                <select
                                    value={platformId}
                                    onChange={(e) => setPlatformId(e.target.value)}
                                    className="w-full bg-background border border-border/50 rounded-md p-2 text-foreground focus:outline-none focus:border-primary"
                                >
                                    <option value="">Select Platform</option>
                                    {platforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>

                            <div className="pt-2">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-muted-foreground">Time Period</label>
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={platformAllData}
                                            onChange={(e) => setPlatformAllData(e.target.checked)}
                                            className="form-checkbox h-4 w-4 text-primary bg-background border-border/50 rounded"
                                        />
                                        <span className="text-xs text-muted-foreground">All Time</span>
                                    </label>
                                </div>
                                <MonthPicker 
                                    value={platformMonth} 
                                    onChange={date => setPlatformMonth(date)} 
                                    disabled={platformAllData}
                                />
                            </div>
                        </div>
                        <button
                            onClick={() => handleDeleteClick('platform')}
                            disabled={loading || !platformId || (!platformAllData && !platformMonth)}
                            className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FaTrash className="mr-2" /> Delete Platform Data
                        </button>
                    </div>

                    {/* Delete by Label */}
                    <div className="bg-gradient-card border border-border/50 p-6 rounded-lg shadow-lg flex flex-col h-full">
                        <div className="mb-4">
                            <h3 className="text-lg font-semibold text-foreground mb-1">3. Delete by Label</h3>
                            <p className="text-xs text-muted-foreground">Removes data for a specific label, either for a specific month or all time.</p>
                        </div>
                        <div className="flex-grow space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Select Label</label>
                                <SearchableSelect
                                    options={labels.map(a => ({ value: a, label: a }))}
                                    value={labelName}
                                    onChange={setLabelName}
                                    placeholder="Search Label..."
                                />
                            </div>
                            
                            <div className="pt-2">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-muted-foreground">Time Period</label>
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={labelAllData}
                                            onChange={(e) => setLabelAllData(e.target.checked)}
                                            className="form-checkbox h-4 w-4 text-primary bg-background border-border/50 rounded"
                                        />
                                        <span className="text-xs text-muted-foreground">All Time</span>
                                    </label>
                                </div>
                                <MonthPicker 
                                    value={labelMonth} 
                                    onChange={date => setLabelMonth(date)} 
                                    disabled={labelAllData}
                                />
                            </div>
                        </div>
                        <button
                            onClick={() => handleDeleteClick('label')}
                            disabled={loading || !labelName || (!labelAllData && !labelMonth)}
                            className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FaTrash className="mr-2" /> Delete Label Data
                        </button>
                    </div>

                    {/* Delete by Artist */}
                    <div className="bg-gradient-card border border-border/50 p-6 rounded-lg shadow-lg flex flex-col h-full">
                        <div className="mb-4">
                            <h3 className="text-lg font-semibold text-foreground mb-1">4. Delete by Artist Name</h3>
                            <p className="text-xs text-muted-foreground">Removes data for a specific artist, either for a specific month or all time.</p>
                        </div>
                        <div className="flex-grow space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Select Artist</label>
                                <SearchableSelect
                                    options={artistNames.map(a => ({ value: a, label: a }))}
                                    value={artistName}
                                    onChange={setArtistName}
                                    placeholder="Search Artist..."
                                />
                            </div>

                            <div className="pt-2">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-muted-foreground">Time Period</label>
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={artistAllData}
                                            onChange={(e) => setArtistAllData(e.target.checked)}
                                            className="form-checkbox h-4 w-4 text-primary bg-background border-border/50 rounded"
                                        />
                                        <span className="text-xs text-muted-foreground">All Time</span>
                                    </label>
                                </div>
                                <MonthPicker 
                                    value={artistMonth} 
                                    onChange={date => setArtistMonth(date)} 
                                    disabled={artistAllData}
                                />
                            </div>
                        </div>
                        <button
                            onClick={() => handleDeleteClick('artist')}
                            disabled={loading || !artistName || (!artistAllData && !artistMonth)}
                            className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FaTrash className="mr-2" /> Delete Artist Data
                        </button>
                    </div>

                    {/* Delete Artist Account */}
                    <div className="bg-gradient-to-br from-red-900/50 to-red-800/30 border border-red-700/50 p-6 rounded-lg shadow-lg flex flex-col h-full">
                        <div className="mb-4">
                            <h3 className="text-lg font-semibold text-red-300 mb-1">5. Delete Artist Account</h3>
                            <p className="text-xs text-red-400">Permanently deletes a registered artist's account and all their data.</p>
                        </div>
                        <div className="flex-grow space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Select Registered Artist</label>
                                <SearchableSelect
                                    options={registeredArtists.map(a => ({ value: a.pk, label: a.name }))}
                                    value={artistIdToDelete}
                                    onChange={setArtistIdToDelete}
                                    placeholder="Search Registered Artist..."
                                />
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                if (artistIdToDelete) {
                                    setShowDeleteArtistModal(true);
                                }
                            }}
                            disabled={loading || !artistIdToDelete}
                            className="w-full mt-6 bg-red-800 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FaDumpster className="mr-2" /> Delete Artist Account
                        </button>
                    </div>
                </div>
            </div>

            {/* Delete Artist Account Confirmation Modal */}
            {showDeleteArtistModal && (
                <div className="fixed inset-0 bg-black/80 flex justify-center items-center z-50 p-4">
                    <div className="bg-card border-2 border-red-700 rounded-lg shadow-xl max-w-md w-full p-6 animate-scale-in">
                        <div className="flex items-center text-red-500 mb-4">
                            <FaExclamationTriangle size={32} className="mr-4" />
                            <h3 className="text-2xl font-bold">DANGER ZONE</h3>
                        </div>
                        
                        <p className="text-foreground mb-4">You are about to permanently delete the artist account for:</p>
                        <p className="text-xl font-bold text-red-400 text-center py-2 bg-secondary/50 rounded-md mb-4">
                            {registeredArtists.find(a => a.pk === artistIdToDelete)?.name}
                        </p>
                        
                        <p className="text-muted-foreground text-sm mb-6">
                            This will delete the artist's login credentials, all associated analytics data, withdrawal requests, and personal information. This action is irreversible.
                        </p>
                        
                        <div className="flex justify-end space-x-3">
                            <button 
                                onClick={() => setShowDeleteArtistModal(false)}
                                className="px-4 py-2 rounded-md bg-secondary text-foreground hover:bg-secondary/80 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleConfirmDeleteArtist}
                                className="px-4 py-2 rounded-md bg-red-700 text-white hover:bg-red-800 transition-colors font-bold flex items-center"
                            >
                                <FaDumpster className="mr-2" /> I understand, proceed.
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageDataPage;
